/**
 * Admin Activation page component.
 *
 * Route: `/admin/activation`
 * Requires authentication: Yes (admin only)
 *
 * @module AdminActivationPage
 */

import { useState, useEffect } from 'react';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import {
  Loader2,
  KeyRound,
  Copy,
  RefreshCcw,
  Trash2,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  getActivationCodes,
  generateActivationCodes,
  deleteActivationCode,
  type ActivationCode,
  type ActivationSummary,
} from '@/lib/activationApi';
import { logger } from '@/lib/logger';

export default function AdminActivationPage() {
  const [count, setCount] = useState('10');
  const [isGenerating, setIsGenerating] = useState(false);
  const [isLoadingList, setIsLoadingList] = useState(false);
  const [codes, setCodes] = useState<ActivationCode[]>([]);
  const [summary, setSummary] = useState<ActivationSummary>({
    total: 0,
    used: 0,
    unused: 0,
  });
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deletingCode, setDeletingCode] = useState<ActivationCode | null>(null);

  const fetchActivationCodes = async () => {
    setIsLoadingList(true);
    try {
      const data = await getActivationCodes();
      setCodes(data.codes || []);
      setSummary(data.summary || { total: 0, used: 0, unused: 0 });
    } catch (error: any) {
      // Error already handled in API function
      logger.error('Failed to fetch activation codes:', error);
    } finally {
      setIsLoadingList(false);
    }
  };

  useEffect(() => {
    fetchActivationCodes();
  }, []);

  const handleGenerate = async () => {
    const parsedCount = Number(count);
    if (Number.isNaN(parsedCount) || parsedCount < 1) {
      toast.error('Enter a valid count (1-100)');
      return;
    }
    setIsGenerating(true);
    try {
      await generateActivationCodes(parsedCount);
      toast.success(`Generated ${parsedCount} activation codes`);
      await fetchActivationCodes();
    } catch (error: any) {
      // Error already handled in API function
      logger.error('Failed to generate activation codes:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = (code: string) => {
    navigator.clipboard
      ?.writeText(code)
      .then(() => {
        toast.success('Code copied to clipboard');
      })
      .catch(() => {
        toast.error('Failed to copy code');
      });
  };

  const handleDeleteClick = (code: ActivationCode) => {
    setDeletingCode(code);
    setShowDeleteDialog(true);
  };

  const handleDelete = async () => {
    if (!deletingCode) return;

    try {
      await deleteActivationCode(deletingCode.id);
      toast.success('Activation code deleted successfully');
      setShowDeleteDialog(false);
      setDeletingCode(null);
      await fetchActivationCodes();
    } catch (error: any) {
      // Error already handled in API function
      logger.error('Failed to delete activation code:', error);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg font-ui">
            <KeyRound className="h-5 w-5 text-primary" />
            Activation Codes
          </CardTitle>
          <CardDescription className="font-ui">
            Generate unique activation codes for onboarding or private betas.
          </CardDescription>
        </CardHeader>

        <CardContent className="space-y-6">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="space-y-2 max-w-xs">
              <Label htmlFor="activationCount" className="font-ui">
                Number of codes
              </Label>
              <Input
                id="activationCount"
                type="number"
                min={1}
                max={100}
                value={count}
                onChange={(e) => setCount(e.target.value)}
                placeholder="Enter a number between 1 and 100"
              />
              <p className="text-xs text-muted-foreground font-ui">
                Codes are 5-character alphanumeric strings without ambiguous
                characters.
              </p>
            </div>

            <div className="flex items-end">
              <Button
                onClick={handleGenerate}
                disabled={isGenerating}
                className="w-full"
              >
                {isGenerating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    <span className="font-ui">Generating...</span>
                  </>
                ) : (
                  <>
                    <KeyRound className="h-4 w-4 mr-2" />
                    <span className="font-ui">Generate Codes</span>
                  </>
                )}
              </Button>
            </div>
          </div>

          {codes.length > 0 && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground font-ui">
                      Total Codes
                    </p>
                    <p className="text-2xl font-bold font-ui">{summary.total}</p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground font-ui">
                      Available
                    </p>
                    <p className="text-2xl font-bold text-primary font-ui">
                      {summary.unused}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground font-ui">Used</p>
                    <p className="text-2xl font-bold text-muted-foreground font-ui">
                      {summary.used}
                    </p>
                  </CardContent>
                </Card>
                <Card>
                  <CardContent className="pt-4">
                    <p className="text-sm text-muted-foreground font-ui">
                      Last Refresh
                    </p>
                    <p className="text-2xl font-bold text-foreground font-ui">
                      {new Date().toLocaleTimeString()}
                    </p>
                  </CardContent>
                </Card>
              </div>

              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-medium text-foreground font-ui">
                    Generated Codes
                  </h3>
                  <p className="text-xs text-muted-foreground font-ui">
                    Newly generated codes are unused by default.
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary" className="font-ui">
                    {codes.length} codes
                  </Badge>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={fetchActivationCodes}
                    disabled={isLoadingList}
                  >
                    {isLoadingList ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCcw className="h-4 w-4 mr-1" />
                        <span className="font-ui">Refresh</span>
                      </>
                    )}
                  </Button>
                </div>
              </div>


              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="font-ui">Code</TableHead>
                      <TableHead className="font-ui">Status</TableHead>
                      <TableHead className="font-ui">Created</TableHead>
                      <TableHead className="font-ui">Used</TableHead>
                      <TableHead className="font-ui">Used by</TableHead>
                      <TableHead className="w-24 text-right font-ui">
                        Actions
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {codes.map((code) => (
                      <TableRow key={code.id}>
                        <TableCell className="font-mono text-sm">
                          {code.code}
                        </TableCell>
                        <TableCell>
                          <Badge
                            variant={code.is_used ? 'outline' : 'default'}
                            className="font-ui"
                          >
                            {code.is_used ? 'Used' : 'Unused'}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground font-ui">
                          {code.created_at
                            ? new Date(code.created_at).toLocaleString()
                            : '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground font-ui">
                          {code.used_at
                            ? new Date(code.used_at).toLocaleString()
                            : '—'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground font-ui">
                          {code.used_by_username ?? '—'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleCopy(code.code)}
                              title="Copy code"
                            >
                              <Copy className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteClick(code)}
                              title="Delete code"
                              className="text-destructive hover:text-destructive"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-ui">
              Delete Activation Code
            </AlertDialogTitle>
            <AlertDialogDescription className="font-ui">
              Are you sure you want to delete code "{deletingCode?.code}"? This
              action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>
              <span className="font-ui">Cancel</span>
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              <span className="font-ui">Delete</span>
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
